import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { IonCard } from '@ionic/angular/standalone';
import * as L from 'leaflet';

export interface LocationCoordinates {
  latitude: number;
  longitude: number;
}

@Component({
  selector: 'mtjr-location-map',
  imports: [IonCard],
  template: `
    <ion-card
      class="mx-0 rounded-lg overflow-hidden shadow-lg transition-all duration-300"
      [class.fullscreen-map]="isFullscreen()"
    >
      <div
        #mapContainer
        class="w-full relative cursor-crosshair transition-all duration-300"
        [class.h-64]="!isFullscreen()"
        [class.h-screen]="isFullscreen()"
        [class.opacity-50]="disabled"
      >
        @if (!coordinates) {
          <div class="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <div class="text-center p-4">
              <div class="text-gray-500 mb-2">
                <svg class="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <p class="text-sm text-gray-600">Click on the map to select location</p>
            </div>
          </div>
        }
      </div>
    </ion-card>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      :host ::ng-deep .leaflet-bar {
        border-radius: 8px;
      }

      :host ::ng-deep .leaflet-touch .leaflet-bar a:first-child {
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
      }

      :host ::ng-deep .leaflet-touch .leaflet-bar a:last-child {
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
      }

      .leaflet-container {
        border-radius: 8px;
      }

      .leaflet-control-container .leaflet-top.leaflet-right {
        top: 10px;
        right: 10px;
      }

      .leaflet-control-zoom {
        border: none;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }

      .leaflet-control-zoom a {
        background-color: white;
        color: #374151;
        border-radius: 8px;
        margin: 1px;
        font-weight: 600;
        transition: all 0.2s ease;
      }

      .leaflet-control-zoom a:hover {
        background-color: #f3f4f6;
        color: #1f2937;
      }

      :host-context(.fullscreen-map) {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999;
        margin: 0;
        border-radius: 0;
      }

      .fullscreen-map {
        position: fixed !important;
        inset: 0 !important;
        z-index: 9999 !important;
        margin: 0 !important;
        border-radius: 0 !important;
        width: 100% !important;
        height: 100% !important;
        max-width: 100vw !important;
        max-height: 100vh !important;
        overflow: hidden !important;
      }

      .fullscreen-map .leaflet-container {
        border-radius: 0;
      }

      :host ::ng-deep .fullscreen-map .leaflet-top {
        top: 70px !important;
        z-index: 10000 !important;
      }

      :host ::ng-deep .fullscreen-map .leaflet-right {
        right: 10px !important;
        z-index: 10000 !important;
      }

      :host ::ng-deep .fullscreen-map .leaflet-bottom {
        bottom: 70px !important;
        z-index: 10000 !important;
      }

      :host ::ng-deep .fullscreen-map .leaflet-control {
        z-index: 10000 !important;
      }

      :host ::ng-deep .fullscreen-map .locate-control {
        transform: scale(1.25) !important;
        margin-top: 20px !important;
      }

      :host ::ng-deep .fullscreen-map .leaflet-control.fullscreen-control {
        transform: scale(1.25) !important;
      }

      :host ::ng-deep .fullscreen-map .leaflet-control-zoom {
        transform: scale(1.25) !important;
      }
    `,
  ],
})
export class LocationMapComponent implements OnInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;
  @Input() coordinates: LocationCoordinates | null = null;
  @Input() disabled = false;
  @Output() locationSelected = new EventEmitter<LocationCoordinates>();

  isFullscreen = signal(false);
  private map: L.Map | null = null;
  private marker: L.Marker | null = null;

  ngOnInit() {
    this.initializeMap();
  }

  ngOnDestroy() {
    if (this.map) {
      this.map.remove();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isFullscreen()) {
      this.toggleFullscreen();
    }
  }

  toggleFullscreen() {
    this.isFullscreen.update((v) => !v);
    // Invalidate map size after transition
    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 350);
  }

  private fixLeafletIcons() {
    // Fix for Leaflet default markers not showing
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    });
  }

  private initializeMap() {
    // Fix Leaflet default markers
    this.fixLeafletIcons();
    // 27.645, 17.600
    // Default center (you can change this to your preferred default location)
    const defaultLat = this.coordinates?.latitude || 27.645; // Tripoli, Libya
    const defaultLng = this.coordinates?.longitude || 17.6;

    // Initialize the map
    this.map = L.map(this.mapContainer.nativeElement, {
      center: [defaultLat, defaultLng],
      zoom: this.coordinates ? 15 : 4,
      zoomControl: true,
      attributionControl: false,
    });

    // Add tile layer with better configuration
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors',
      crossOrigin: true,
      detectRetina: true,
    }).addTo(this.map);

    // Add custom attribution control
    L.control
      .attribution({
        position: 'bottomright',
        prefix: false,
      })
      .addAttribution('© OpenStreetMap')
      .addTo(this.map);

    // Add existing marker if coordinates are provided
    if (this.coordinates) {
      this.addMarker(this.coordinates.latitude, this.coordinates.longitude);
    }

    // Add custom locate control using Leaflet's built-in locate
    if (!this.disabled) {
      const locateControl = L.Control.extend({
        onAdd: () => {
          const container = L.DomUtil.create('div', 'leaflet-control leaflet-control-custom locate-control');
          container.style.backgroundColor = 'white';
          container.style.borderRadius = '8px';
          container.style.boxShadow = '2px 2px 2px rgba(60,64,67,0.3),2px 2px 2px rgba(60,64,67,0.15)';

          container.style.color = 'black';
          container.style.width = '32px';
          container.style.height = '32px';
          container.style.paddingBottom = '5px';
          container.style.marginTop = '5px';

          container.style.cursor = 'pointer';
          container.innerHTML = '⦿';
          container.style.fontSize = '32px';
          container.style.display = 'flex';
          container.style.alignItems = 'center';
          container.style.justifyContent = 'center';
          container.title = 'Find my location';

          container.onclick = (e) => {
            e.stopPropagation();
            e.preventDefault();
            this.map?.locate({ setView: true, maxZoom: 16, enableHighAccuracy: true });
          };

          return container;
        },
      });

      new locateControl({ position: 'topleft' }).addTo(this.map);

      // Add fullscreen toggle control
      const fullscreenControl = L.Control.extend({
        onAdd: () => {
          const container = L.DomUtil.create('div', 'leaflet-control leaflet-control-custom fullscreen-control');
          container.style.backgroundColor = 'white';
          container.style.borderRadius = '8px';
          container.style.boxShadow = '0 1px 2px rgba(60,64,67,0.3),0 1px 3px 1px rgba(60,64,67,0.15)';
          container.style.width = '30px';
          container.style.height = '30px';
          container.style.cursor = 'pointer';
          container.style.display = 'flex';
          container.style.alignItems = 'center';
          container.style.justifyContent = 'center';
          container.title = 'Toggle fullscreen';

          // Fullscreen icon (expand arrows)
          container.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
            </svg>
          `;

          container.onclick = (e) => {
            e.stopPropagation();
            e.preventDefault();
            this.toggleFullscreen();
            // Update icon based on state
            if (this.isFullscreen()) {
              container.innerHTML = `
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 14h6v6m10-10h-6V4m0 6 7-7M3 21l7-7"/>
                </svg>
              `;
              container.title = 'Exit fullscreen (Esc)';
            } else {
              container.innerHTML = `
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                </svg>
              `;
              container.title = 'Toggle fullscreen';
            }
          };

          return container;
        },
      });

      new fullscreenControl({ position: 'topright' }).addTo(this.map);

      // Listen for location found event
      this.map.on('locationfound', (e: any) => {
        const { lat, lng } = e.latlng;
        this.addMarker(lat, lng);
        this.locationSelected.emit({ latitude: lat, longitude: lng });
      });

      // Handle location error
      this.map.on('locationerror', () => {
        console.warn('Unable to retrieve your location');
      });
    }

    // Add click event listener
    if (!this.disabled) {
      this.map.on('click', (e: L.LeafletMouseEvent) => {
        const { lat, lng } = e.latlng;
        this.addMarker(lat, lng);
        this.locationSelected.emit({ latitude: lat, longitude: lng });
      });
    }

    // Style the map container
    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 100);
  }

  private addMarker(lat: number, lng: number) {
    if (!this.map) return;

    // Remove existing marker
    if (this.marker) {
      this.map.removeLayer(this.marker);
    }

    // Create custom marker icon
    const customIcon = L.divIcon({
      className: 'custom-marker',
      html: `
        <div class="relative">
          <div class="w-6 h-6 bg-blue-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
            <div class="w-2 h-2 bg-white rounded-full"></div>
          </div>
          <div class="absolute top-0 left-0 w-6 h-6 bg-blue-400 rounded-full animate-ping opacity-75"></div>
        </div>
      `,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });

    // Add new marker
    this.marker = L.marker([lat, lng], { icon: customIcon }).addTo(this.map);

    // Center map on marker
    this.map.setView([lat, lng], Math.max(this.map.getZoom(), 15));
  }

  public updateLocation(coordinates: LocationCoordinates) {
    this.coordinates = coordinates;
    if (this.map) {
      this.addMarker(coordinates.latitude, coordinates.longitude);
    }
  }
}
